import { useEffect, useState } from "react";
import { api, pillClass, statusLabel, fmtDate, isOverdue, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { CheckCircle2, RotateCcw, Download, Plus } from "lucide-react";
import { toast } from "sonner";

export default function ActionsPage() {
  const [items, setItems] = useState([]);
  const [members, setMembers] = useState([]);
  async function load() {
    const r = await api.get("/actions"); setItems(r.data);
    try { const d = await api.get("/directory"); setMembers(d.data.users || []); } catch (err) { console.error("directory load failed", err); }
  }
  useEffect(() => { load(); }, []);

  async function markDone(id) {
    try { await api.patch(`/actions/${id}`, { status: "done" }); load(); toast.success("Marked done"); }
    catch (e) { toast.error(formatApiError(e)); }
  }

  async function reopen(id) {
    try { await api.post(`/actions/${id}/reopen`); load(); toast.success("Reopened"); }
    catch (e) { toast.error(formatApiError(e)); }
  }

  const buckets = {
    overdue: items.filter((a) => isOverdue(a.due_date, a.status)),
    pending: items.filter((a) => !isOverdue(a.due_date, a.status) && (a.status === "pending" || a.status === "in_progress")),
    done: items.filter((a) => a.status === "done"),
  };

  return (
    <div className="px-5 md:px-10 py-6 md:py-10 max-w-3xl">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-stone-500 font-bold">To-do tracker</div>
          <h1 className="font-serif-display text-3xl md:text-4xl font-semibold mt-1">What needs doing</h1>
          <p className="text-stone-600 text-sm mt-1">To-dos are everything that came out of meetings, issues, or were added by hand. We used to call these "actions".</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <AddTodoDialog members={members} onDone={load} />
          <button onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/export/actions`, "_blank")}
            data-testid="actions-export"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-stone-300 text-sm hover:border-stone-500"><Download size={14} /> Export</button>
        </div>
      </div>

      <Group title="Overdue" color="text-red-700" items={buckets.overdue} markDone={markDone} reopen={null} testid="actions-overdue" />
      <Group title="Pending & in progress" items={buckets.pending} markDone={markDone} reopen={null} testid="actions-pending" />
      <Group title="Completed" items={buckets.done} markDone={null} reopen={reopen} testid="actions-done" />
      {items.length === 0 && (
        <div className="text-stone-500 italic py-12 text-center" data-testid="todos-empty">No to-dos yet. Add one or hold your first meeting.</div>
      )}
    </div>
  );
}

function AddTodoDialog({ members, onDone }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(""); const [desc, setDesc] = useState("");
  const [owner, setOwner] = useState(""); const [due, setDue] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/actions", {
        title, description: desc,
        owner_user_id: owner || null,
        due_date: due || null,
      });
      toast.success("To-do added");
      setTitle(""); setDesc(""); setOwner(""); setDue("");
      setOpen(false); onDone?.();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" data-testid="add-todo-btn"
          className="rounded-full bg-[#C05640] hover:bg-[#A54734]">
          <Plus size={14} className="mr-1.5" /> Add to-do
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-serif-display">Add a to-do</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2" data-testid="todo-form">
          <div><Label>What needs doing?</Label><Input value={title} required onChange={(e) => setTitle(e.target.value)} data-testid="todo-title" className="h-11 mt-1.5" placeholder="Call the lift AMC and renew" /></div>
          <div><Label>Details (optional)</Label><Textarea rows={2} value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="todo-desc" className="mt-1.5" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Assign to</Label>
              <select value={owner} onChange={(e) => setOwner(e.target.value)} data-testid="todo-owner"
                className="w-full h-11 mt-1.5 rounded-xl border border-stone-300 px-3 bg-white">
                <option value="">— Unassigned —</option>
                {members.map((m) => <option key={m.id} value={m.id}>{m.name} ({m.role})</option>)}
              </select>
            </div>
            <div>
              <Label>Due date</Label>
              <Input type="date" value={due} onChange={(e) => setDue(e.target.value)} data-testid="todo-due" className="h-11 mt-1.5" />
            </div>
          </div>
          <Button type="submit" disabled={busy} data-testid="todo-save"
            className="w-full h-11 rounded-full bg-[#C05640] hover:bg-[#A54734]">{busy ? "Adding…" : "Add to-do"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Group({ title, color = "text-stone-700", items, markDone, reopen, testid }) {
  if (!items.length) return null;
  return (
    <section className="mt-7" data-testid={testid}>
      <h2 className={`text-xs font-bold uppercase tracking-widest ${color} mb-3`}>{title} · {items.length}</h2>
      <div className="space-y-2">
        {items.map((a) => (
          <div key={a.id} className="bg-white border border-stone-200 rounded-2xl p-4 flex items-center gap-4" data-testid={`act-${a.id}`}>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-stone-900">{a.title}</div>
              <div className="text-xs text-stone-500 mt-1 flex items-center gap-2 flex-wrap">
                <span className={pillClass(isOverdue(a.due_date, a.status) ? "overdue" : a.status)}>
                  {isOverdue(a.due_date, a.status) ? "Overdue" : statusLabel(a.status)}
                </span>
                <span>· due {fmtDate(a.due_date)}</span>
                {(a.owner_resolved_name || a.owner_name) && <span>· {a.owner_resolved_name || a.owner_name}</span>}
              </div>
              {a.description && <div className="text-xs text-stone-600 mt-1">{a.description}</div>}
            </div>
            <div className="flex flex-col gap-1 shrink-0">
              {markDone && (
                <Button onClick={() => markDone(a.id)} variant="outline" size="sm" data-testid={`done-${a.id}`} className="rounded-full">
                  <CheckCircle2 size={14} className="mr-1" /> Done
                </Button>
              )}
              {reopen && (
                <Button onClick={() => reopen(a.id)} variant="outline" size="sm" data-testid={`reopen-${a.id}`} className="rounded-full">
                  <RotateCcw size={14} className="mr-1" /> Reopen
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
